import React, { useState, useEffect, useCallback } from 'react';
import { styled } from '@this/constants/themes';

import { reportError } from '@this/lib/bugsnag';
import type Department from '@this/domain/department/department2';

type Props = {
  departments: Department[];
  onSelect: (department: Department) => void;
  disabledAddress: boolean;
  workflowFormWidth: string;
};

const DepartmentsList: React.FC<Props> = ({ departments, onSelect, disabledAddress, workflowFormWidth }) => {
  const [filteredDepartments, setFilteredDepartments] = useState<Department[]>(departments);

  useEffect(() => {
    const el = document.getElementById('departments-list-input');
    if (el) el.focus();
  }, []);

  useEffect(() => {
    setFilteredDepartments(departments);
  }, [departments]);

  const handleSelect = useCallback(
    (department: Department) => () => {
      onSelect(department);
    },
    [onSelect]
  );

  const handleSearchDepartments = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const keyword = (e.target as HTMLInputElement).value;
      const filtered = departments.filter(
        department =>
          (department.code ?? '').indexOf(keyword) >= 0 || (department.name ?? '').indexOf(keyword) >= 0
      );
      setFilteredDepartments(filtered);
    },
    [departments]
  );

  try {
    return (
      <DepartmentsListItemLists>
        <DepartmentListsInput
          type="text"
          id="departments-list-input"
          defaultValue=""
          placeholder="検索"
          onKeyUp={handleSearchDepartments}
        />
        <DepartmentsListUl workflowFormWidth={workflowFormWidth}>
          {filteredDepartments.map(department =>
            department.noAddress && disabledAddress ? (
              <DepartmentsListItemDisabled key={department.id}>
                <DepartmentsListItemInfo>
                  <p>{department.description}</p>
                </DepartmentsListItemInfo>
              </DepartmentsListItemDisabled>
            ) : (
              <DepartmentsListItemEnabled key={department.id} onClick={handleSelect(department)}>
                <DepartmentsListItemInfo>
                  <p>{disabledAddress ? department.description : department.descriptionWithCode}</p>
                </DepartmentsListItemInfo>
              </DepartmentsListItemEnabled>
            )
          )}
        </DepartmentsListUl>
      </DepartmentsListItemLists>
    );
  } catch (e) {
    reportError(e);
    return null;
  }
};

const DepartmentsListItemLists = styled.div`
  margin-top: 5px;
`;

const DepartmentListsInput = styled.input`
  &&& {
    width: 100%;
    margin-bottom: 0;
    border-top: none;
  }
`;

const DepartmentsListUl = styled.ul<{ workflowFormWidth?: string }>`
  overflow-y: scroll;
  max-height: 150px;
  width: ${props => props.workflowFormWidth || '420px'};
  border-bottom: 2px solid ${props => props.theme.grayBorderColor};
`;

const DepartmentsListItemDisabled = styled.li`
  background: ${props => props.theme.fieldBgColor};
  min-height: 30px;
  max-height: 60px;
  width: 100%;
  line-height: 30px;
  padding: 0 10px;
  background: #eee;
  cursor: default;
  overflow-x: hidden !important;
  text-overflow: ellipsis;
  white-space: nowrap;
  border: 2px solid ${props => props.theme.grayBorderColor};
  border-top: none;
  z-index: 100;
`;

const DepartmentsListItemEnabled = styled.li`
  background: ${props => props.theme.fieldBgColor};
  min-height: 30px;
  max-height: 60px;
  width: 100%;
  line-height: 30px;
  padding: 0 10px;
  cursor: pointer;
  overflow-x: hidden !important;
  text-overflow: ellipsis;
  white-space: nowrap;
  border: 2px solid ${props => props.theme.grayBorderColor};
  border-top: none;
  z-index: 100;
`;

const DepartmentsListItemInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

export default DepartmentsList;
